import { useState } from 'react';
import { ChartBar, ChartBarStacked, ChartColumn } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import Chart from 'src/componentes/chart';
import PerguntaMetadados from './PerguntaMetadados';
import {
  compararOrdemOpcoes,
  EMPTY_STATE_CLASS_NAME,
  ordenarOpcoes,
  SORT_FORM_ORDER,
  toNumber,
} from './utils';

const MATRIX_CHART_VIEWS = [
  {
    type: 'stackedbar2d',
    labelId: 'labelExibirBarrasHorizontaisEmpilhadas',
    icon: ChartBarStacked,
  },
  {
    type: 'mscolumn2d',
    labelId: 'labelExibirBarrasVerticais',
    icon: ChartColumn,
  },
  { type: 'msbar2d', labelId: 'labelExibirBarrasHorizontais', icon: ChartBar },
];

function MatrizResultados({ intl, pergunta }) {
  const [indiceVisualizacao, setIndiceVisualizacao] = useState(0);
  const visualizacao = MATRIX_CHART_VIEWS[indiceVisualizacao];
  const proximaVisualizacao =
    MATRIX_CHART_VIEWS[(indiceVisualizacao + 1) % MATRIX_CHART_VIEWS.length];
  const ProximaVisualizacaoIcon = proximaVisualizacao.icon;
  const orientacaoLabel = intl.formatMessage({
    id: proximaVisualizacao.labelId,
  });
  const barrasHorizontais = ['msbar2d', 'stackedbar2d'].includes(
    visualizacao.type
  );
  const barrasEmpilhadas = visualizacao.type === 'stackedbar2d';
  const linhas = [...(pergunta.linhas || [])].sort(compararOrdemOpcoes);
  const limitePercentual = barrasEmpilhadas
    ? Math.max(
        100,
        ...linhas.map((linha) =>
          (linha.colunas || []).reduce(
            (total, coluna) => total + toNumber(coluna.percentual),
            0
          )
        )
      )
    : 100;
  const colunasPorId = new Map();
  linhas.forEach((linha) => {
    (linha.colunas || []).forEach((coluna) => {
      const chave = String(coluna.opcaoId);
      if (!colunasPorId.has(chave)) colunasPorId.set(chave, coluna);
    });
  });
  const colunas = ordenarOpcoes([...colunasPorId.values()], SORT_FORM_ORDER);
  const possuiRespostas = linhas.some((linha) =>
    (linha.colunas || []).some((coluna) => toNumber(coluna.quantidade) > 0)
  );
  const dataset = colunas.map((coluna) => ({
    seriesname: coluna.texto,
    color: coluna.corGrafico,
    data: linhas.map((linha) => {
      const resultado = (linha.colunas || []).find(
        (item) => String(item.opcaoId) === String(coluna.opcaoId)
      );
      const valorPercentual = toNumber(resultado?.percentual);
      const percentual = intl.formatNumber(valorPercentual, {
        maximumFractionDigits: 2,
      });
      const percentualBarra = intl.formatNumber(valorPercentual, {
        maximumFractionDigits:
          valorPercentual > 0 && valorPercentual < 0.1 ? 2 : 1,
      });
      return {
        value: resultado ? valorPercentual : '',
        displayValue: `${percentualBarra}%`,
        showValue: valorPercentual > 0 ? '1' : '0',
        toolText: `${linha.texto} · ${
          coluna.texto
        }: ${percentual}% · ${intl.formatNumber(
          toNumber(resultado?.quantidade)
        )} ${intl.formatMessage({
          id: 'labelRespostas',
        })}<br/>${intl.formatNumber(
          toNumber(linha.totalRespostas)
        )} ${intl.formatMessage({
          id: 'labelRespostas',
        })} · ${intl.formatNumber(
          toNumber(linha.totalSelecoes)
        )} ${intl.formatMessage({ id: 'labelSelecoes' })}`,
      };
    }),
  }));

  return (
    <div className="tw:min-w-0 tw:px-5 tw:pt-3.5 tw:pb-5">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
        <div className="tw:min-w-0">
          <PerguntaMetadados intl={intl} pergunta={pergunta} />
          <p className="tw:mt-1 tw:mb-0 tw:text-xs tw:text-[#667085]">
            {intl.formatMessage({
              id: pergunta.permiteMultiplasOpcoes
                ? 'labelMultiplasSelecoesPorLinha'
                : 'labelUmaSelecaoPorLinha',
            })}
          </p>
        </div>
        {possuiRespostas && (
          <Button
            aria-label={orientacaoLabel}
            className="tw:cursor-pointer tw:border-solid tw:border-[#dfe3e8] tw:text-[#1d7789]"
            onClick={() =>
              setIndiceVisualizacao(
                (atual) => (atual + 1) % MATRIX_CHART_VIEWS.length
              )
            }
            size="icon-sm"
            title={orientacaoLabel}
            type="button"
            variant="outline"
          >
            <ProximaVisualizacaoIcon aria-hidden="true" />
          </Button>
        )}
      </div>
      {possuiRespostas ? (
        <div
          aria-label={pergunta.titulo}
          className="tw:min-w-0 tw:overflow-x-auto"
          role="img"
        >
          <Chart
            key={visualizacao.type}
            containerBackgroundOpacity="0"
            dataFormat="json"
            dataSource={{
              chart: {
                animation: '0',
                bgAlpha: '0',
                canvasBgAlpha: '0',
                chartBottomMargin: '8',
                chartLeftMargin: '0',
                chartRightMargin: '16',
                chartTopMargin: '24',
                decimals: '1',
                formatNumberScale: '0',
                labelDisplay: 'wrap',
                labelFontColor: '#344054',
                labelFontSize: '13',
                legendBgAlpha: '0',
                legendBorderAlpha: '0',
                legendPosition: 'bottom',
                ...(barrasHorizontais
                  ? { maxBarHeight: '28', maxLabelWidthPercent: '35' }
                  : { maxColWidth: '42' }),
                numberSuffix: '%',
                ...(barrasEmpilhadas
                  ? {
                      showSum: '0',
                      stack100Percent: '0',
                      valueBgColor: '#ffffff',
                      valueBgAlpha: '90',
                      valueBorderPadding: '2',
                      valueBorderRadius: '2',
                    }
                  : { placeValuesInside: '0' }),
                plotPaddingPercent: '25',
                plotSpacePercent: '30',
                showAlternateHGridColor: '0',
                showAlternateVGridColor: '0',
                showBorder: '0',
                showCanvasBorder: '0',
                showLegend: '1',
                showPlotBorder: '0',
                showValues: '1',
                theme: 'fusion',
                usePlotGradientColor: '0',
                valueFontBold: '1',
                valueFontColor: '#344054',
                valueFontSize: '12',
                valuePadding: '8',
                xAxisName: intl.formatMessage({ id: 'labelMatrizLinhas' }),
                yAxisName: intl.formatMessage({ id: 'labelPercentual' }),
                yAxisMinValue: '0',
                yAxisMaxValue: String(limitePercentual),
              },
              categories: [
                {
                  category: linhas.map((linha) => ({
                    label: `${linha.posicao ?? toNumber(linha.ordem) + 1}. ${
                      linha.texto
                    }`,
                  })),
                },
              ],
              dataset,
            }}
            height={
              barrasHorizontais
                ? Math.max(
                    400,
                    linhas.length *
                      (barrasEmpilhadas ? 56 : colunas.length * 32 + 48) +
                      96
                  )
                : 400
            }
            type={visualizacao.type}
            width={
              barrasHorizontais
                ? '100%'
                : Math.max(720, linhas.length * (colunas.length * 72 + 48))
            }
          />
        </div>
      ) : (
        <div className={EMPTY_STATE_CLASS_NAME}>
          {intl.formatMessage({ id: 'labelNenhumaRespostaParaExibir' })}
        </div>
      )}
    </div>
  );
}

export default MatrizResultados;
